import {
  add,
  attribute,
  clamp,
  div,
  float,
  fwidth,
  max,
  min,
  mix,
  mul,
  remap,
  smoothstep,
  sub,
  texture,
  uniform,
  uv,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { NodeMaterial, UniformNode } from "three/webgpu"

interface MSDFTextNodeMaterialOptions {
  transparent: boolean
  opacity: number
  threshold: number
  color: THREE.ConstNode<THREE.Color>
  strokeColor: string
  strokeOutsetWidth: number
  strokeInsetWidth: number
  isSmooth: number
  map: THREE.Texture
  progress: UniformNode<number>
  frequency: UniformNode<number>
  amplitude: UniformNode<number>
  alphaOffset: UniformNode<number>
  alphaMargin: UniformNode<number>
  time: UniformNode<number>
  burnColor1: UniformNode<THREE.Color>
  burnColor2: UniformNode<THREE.Color>
  fade: UniformNode<boolean>
  noiseTexture: THREE.Texture
  /** Scale of layout UVs when sampling noise (higher = more repetition). Default 1 */
  noiseUvFrequency?: number | UniformNode<number>
  /** Multiplier for sampled noise value. Default 1 */
  noiseUvAmplitude?: number | UniformNode<number>
}

const defaultOptions: Partial<MSDFTextNodeMaterialOptions> = {
  transparent: true,
  opacity: 1,
  threshold: 0.2,
  isSmooth: 0,
  map: new THREE.Texture(),
  fade: uniform(true),
  noiseTexture: new THREE.Texture(),
  noiseUvFrequency: 1,
  noiseUvAmplitude: 1,
}

export class MSDFTextNodeMaterial extends NodeMaterial {
  transparent: boolean
  alpha: UniformNode<number>
  color: THREE.Node | THREE.UniformNode<THREE.Color>
  map: THREE.Texture
  noiseTexture: THREE.Texture
  isSmooth: UniformNode<number>
  threshold: UniformNode<number>
  strokeColor: THREE.UniformNode<THREE.Color>
  strokeOutsetWidth: UniformNode<number>
  strokeInsetWidth: UniformNode<number>
  progress: UniformNode<number>

  fade: UniformNode<boolean>
  noiseUvFrequency: UniformNode<number>
  noiseUvAmplitude: UniformNode<number>

  constructor(options: Partial<MSDFTextNodeMaterialOptions> = {}) {
    const mergedOptions = Object.assign(
      JSON.parse(JSON.stringify(defaultOptions)),
      options,
    ) as MSDFTextNodeMaterialOptions

    super()

    /**
     * Build in properties
     */
    this.transparent = mergedOptions.transparent
    this.progress = mergedOptions.progress

    this.color = mergedOptions.color as unknown as THREE.Node

    this.fade = mergedOptions.fade

    this.colorNode = this.color

    /**
     * Uniforms: basic
     */
    this.alpha = uniform(mergedOptions.opacity)
    this.map = mergedOptions.map
    this.noiseTexture = mergedOptions.noiseTexture
    this.noiseTexture.wrapS = THREE.RepeatWrapping
    this.noiseTexture.wrapT = THREE.RepeatWrapping
    this.noiseUvFrequency =
      typeof mergedOptions.noiseUvFrequency === "object" &&
      mergedOptions.noiseUvFrequency !== null &&
      "value" in mergedOptions.noiseUvFrequency
        ? (mergedOptions.noiseUvFrequency as UniformNode<number>)
        : uniform((mergedOptions.noiseUvFrequency as number) ?? 1)
    this.noiseUvAmplitude =
      typeof mergedOptions.noiseUvAmplitude === "object" &&
      mergedOptions.noiseUvAmplitude !== null &&
      "value" in mergedOptions.noiseUvAmplitude
        ? (mergedOptions.noiseUvAmplitude as UniformNode<number>)
        : uniform((mergedOptions.noiseUvAmplitude as number) ?? 1)

    /**
     * Uniforms small font sizes
     */
    this.isSmooth = uniform(mergedOptions.isSmooth)
    this.threshold = uniform(mergedOptions.threshold)

    /**
     * Uniforms: stroke
     */
    this.strokeColor = uniform(new THREE.Color(mergedOptions.strokeColor))
    this.strokeOutsetWidth = uniform(mergedOptions.strokeOutsetWidth)
    this.strokeInsetWidth = uniform(mergedOptions.strokeInsetWidth)

    const afwidth = 1.4142135623730951 / 2.0
    const median = (r: THREE.Node, g: THREE.Node, b: THREE.Node) =>
      max(min(r, g), min(max(r, g), b))

    /**
     * Texture Sampling
     */
    const s = texture(this.map, uv())

    /**
     * Fill
     */
    const sigDist = sub(median(s.r, s.g, s.b), 0.5)
    let alpha = clamp(add(div(sigDist, fwidth(sigDist)), 0.5), 0.0, 1.0)

    /**
     * Fill Smooth
     */
    // For smooth rendering, use a range that ensures smoothstep reaches 1.0
    // sigDist ranges from ~-0.5 to 0.5, with center of glyphs around 0.3-0.5
    // Adjust smoothMax to be lower so sigDist can exceed it and reach 1.0
    const smoothRange = mul(afwidth, 0.5)
    const smoothMin = sub(this.threshold, smoothRange)
    // Use a smaller upper bound so smoothstep reaches 1.0 for high sigDist
    const smoothMax = uniform(0.3) // Fixed value that sigDist can exceed
    let smoothAlpha = smoothstep(smoothMin, smoothMax, sigDist)

    // Ensure full opacity for high sigDist values (center of glyphs)
    // When sigDist > smoothMax, smoothstep returns 1.0, but we also ensure
    // that very high sigDist values definitely get full opacity
    smoothAlpha = max(
      smoothAlpha,
      smoothstep(uniform(-0.2), uniform(0.2), sigDist),
    )

    alpha = mix(alpha, smoothAlpha, this.isSmooth)

    /**
     * Cut: Vertical mask based on progress
     * Returns 1 at top, 0 at bottom, with smooth gradient near progress value
     */
    const gradientWidth = 0.2
    const tiltAmount = 0.1 // Amount of tilt (adjust for more/less tilt)
    // Use layoutUv attribute which is already normalized (0-1) for the text layout
    // layoutUv.y gives normalized vertical position relative to the text block
    const layoutUvAttr = attribute("layoutUv", "vec2")

    // Noise: sample noiseTexture with layout UVs so it maps across the whole text box (not per character).
    // noiseUvFrequency: scales UVs (higher = more tiles; texture should use RepeatWrapping). noiseUvAmplitude: contrast.
    const noiseUv = mul(layoutUvAttr, this.noiseUvFrequency)
    const noiseRaw = texture(this.noiseTexture, noiseUv).r
    // Remap around 0.5 so amplitude controls contrast (pattern visibility)
    const noiseCentered = sub(noiseRaw, 0.5)
    const noiseContrast = clamp(
      add(mul(noiseCentered, this.noiseUvAmplitude), 0.5),
      0,
      1,
    )
    const noise = noiseContrast

    // Add tilt: offset vertical position based on horizontal position
    // Center x coordinate (-0.5 to 0.5) and multiply by tilt amount
    const horizontalOffset = sub(layoutUvAttr.x, 0.5).mul(tiltAmount)
    const normalizedVerticalPos = add(layoutUvAttr.y, horizontalOffset)

    // prevent gradient from being visible at edges
    const remappedProgress = remap(
      this.progress,
      0,
      1,
      sub(0, gradientWidth),
      add(1, gradientWidth),
    )

    const fadeCut = smoothstep(
      sub(remappedProgress, gradientWidth),
      add(remappedProgress, gradientWidth),
      normalizedVerticalPos,
    ).oneMinus()

    // Use mix to conditionally apply fade: blend between progress-based cut and fade cut
    // Convert boolean fade uniform to float for mix (1.0 if true, 0.0 if false)
    // In shaders, boolean uniforms are typically represented as floats
    const fadeFactor = this.fade as unknown as UniformNode<number>
    // When fade is false: hide when progress is 0, show when progress > 0
    // When fade is true: use the fade cut based on vertical position
    const progressCut = smoothstep(float(0), float(0.001), this.progress)
    const cut = mix(progressCut, fadeCut, fadeFactor)

    this.opacityNode = this.alpha.mul(alpha).mul(noise).mul(cut)
  }
}
